import React from 'react';
import Component from 'app/components/auth/SignupForm';
import { connect } from 'react-redux';
import { createUser as createLocalUser } from 'app/models/entities/actions';
import { signup } from 'app/models/entities/remote';
import { isAuthenticatedSelector } from 'app/models/auth/selectors';
import { useNewUserDefaults } from 'app/components/auth/GetNewUserDefaults';
import { startAuthenticateWithOauth } from 'app/models/auth/actions';
import { RecaptchaMessage, getRecaptchaToken } from 'app/components/auth/Recaptcha';
import { useHistory } from 'app/components/router/History';
import { History } from 'app/services/history';
import { DMMAuthService } from 'app/services/oauth/DMMAuthService';
import { LoginFailure, useLoginContext } from '../../components/auth/LoginContext';
import { LoadingStage } from '../../containers/state/LoadingState';
import User, { IUser } from 'app/constants/schemas/User';
import { AppState } from 'app/store';
import { AppDispatch } from 'redux';
import { SignupSource } from 'app/utils/api/SignupStateParam';
import { warn } from 'app/services/logger';
import OauthError from 'app/services/oauth/OauthError';
import { AuthProviderId } from 'app/constants/enums/AuthProvider/data';
import { FACEBOOK } from 'app/constants/enums/AuthProvider';
import { BrandContextValue, useBrand } from 'app/components/OrganizationContext';
import { EntityFields, EntityStringId } from 'app/types/entity';
import { IIdAction } from 'app/models/entities/actions/next';

const Credentials: 'credentials' = 'credentials';

type SignupType = typeof Credentials | AuthProviderId;

interface IProps {
  createLocalUser: (details: Partial<User>) => IIdAction;
  signup: (id: string, recaptchaToken: string) => Promise<void>;
  isAuthenticated: boolean;
  defaults: Partial<User>;
  brand: BrandContextValue;
  history: History;
  error: unknown;
}


interface IState {
  credentialsLoadingState: LoadingStage;
  facebookLoadingState: LoadingStage;
  userId?: EntityStringId;
  error: unknown;
}

class SignupForm extends React.Component<IProps, IState> {
  public static getDerivedStateFromProps(props: IProps, state: IState) {
    const newState = {};

    if (state.userId === undefined) {
      Object.assign(newState, { userId: props.createLocalUser(props.defaults).id });
    }

    return newState;
  }

  private mounted: boolean = false;

  public constructor(initialProps: IProps) {
    super(initialProps);
    this.state = {
      credentialsLoadingState: LoadingStage.Idle,
      facebookLoadingState: LoadingStage.Idle,
      error: undefined,
    };
  }

  public componentDidMount() {
    this.mounted = true;
  }

  public componentWillUnmount() {
    this.mounted = false;
  }

  public render() {
    let credentialsLoadingState = LoadingStage.Idle;
    let facebookLoadingState = LoadingStage.Idle;
    let error;

    const { userId } = this.state;

    // There might be an error handed in from the LoginProvider, if an
    // oauth signup failed. Or it might be a locally generated error
    // as part of a credentials signup. Assume that a locally
    // generated error is more relevant, or that the assertion that
    // there is no error is stronger.
    if (this.props.error && this.state.error === undefined) {
      error = this.props.error;

      if (error instanceof LoginFailure && error.cause instanceof OauthError) {
        if (error.cause.provider === FACEBOOK) {
          facebookLoadingState = LoadingStage.Error;
        } else {
          warn(`Error from unexpected provider: ${error.cause.provider}`);
        }
      } else {
        warn(`Unexpected injected error during signup`);
      }
    } else {
      ({ credentialsLoadingState, facebookLoadingState, error } = this.state);
    }

    return (
      <React.Fragment>
        <Component
          id={userId!}
          onSubmitCredentials={this.signup}
          onSubmitFacebook={this.signupWithFacebook}
          credentialsLoadingState={credentialsLoadingState}
          facebookLoadingState={facebookLoadingState}
          error={error}
        />

        <RecaptchaMessage />
      </React.Fragment>
    );
  }

  private setLoadingState(state: LoadingStage, signupType: SignupType) {
    if (this.mounted) {
      if (signupType === Credentials) {
        this.setState({ credentialsLoadingState: state, error: null });
      } else {
        this.setState({ facebookLoadingState: state, error: null });
      }
    }
  }

  private signupWithFacebook = (returnUriReference: string) => {
    try {
      this.setLoadingState(LoadingStage.Loading, FACEBOOK.id);
      const dmmAuthService = new DMMAuthService(this.props.history);

      const provider = FACEBOOK;
      const extraParams = {};

      return startAuthenticateWithOauth(
        { provider, extraParams, returnUriReference, brandId: this.props.brand.id },
        { dmmAuthService },
      );
    } catch (e) {
      this.setState({ facebookLoadingState: LoadingStage.Error, error: e });
      throw e;
    }
  };

  private signup = async (id: string) => {
    try {
      const recaptchaToken = await getRecaptchaToken();
      this.setLoadingState(LoadingStage.Loading, Credentials);
      const result = await this.props.signup(id, recaptchaToken);
      this.setLoadingState(LoadingStage.Success, Credentials);
      return result;
    } catch (e) {
      this.setState({ credentialsLoadingState: LoadingStage.Error, error: e });
      throw e;
    }
  };
}

function mapStateToProps(state: AppState) {
  return { isAuthenticated: isAuthenticatedSelector(state) };
}

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  createLocalUser: (details: Partial<EntityFields<IUser>>): IIdAction => dispatch(createLocalUser(details)),
  signup: (id: string, recaptchaToken: string) =>
    dispatch(signup(id, recaptchaToken, { source: SignupSource.Student })),
});

const ConnectedSignUpForm = connect(mapStateToProps, mapDispatchToProps)(SignupForm);

type ReduxInjectedProps = 'createLocalUser' | 'signup' | 'isAuthenticated';
type ContextInjectedProps = 'history' | 'defaults' | 'error' | 'brand';

type InjectedProps = ReduxInjectedProps | ContextInjectedProps;

const SignupFormInjectDependencies: React.SFC<Omit<IProps, InjectedProps>> = (props) => {
  const history = useHistory();
  const defaults = useNewUserDefaults();
  const loginProvider = useLoginContext();
  const brand = useBrand();
  return (
    <ConnectedSignUpForm
      {...props}
      history={history}
      defaults={defaults}
      brand={brand}
      error={loginProvider && loginProvider.error}
    />
  );
};

export default SignupFormInjectDependencies;
